interface MetaLike {
  event?: unknown;
  merged_at?: unknown;
  merged_by?: unknown;
  title?: unknown;
}

interface ArtifactLike {
  ts?: unknown;
  verdict_state?: unknown;
  cost_usd?: unknown;
  title?: unknown;
  author?: unknown;
  branch?: unknown;
}

interface Summary {
  isMerged: boolean;
  mergedAt: string | null;
  mergedBy: string | null;
  title: string | null;
  author: string | null;
  branch: string | null;
  latestVerdict: string | null;
  totalCostUsd: number;
  runsCount: number;
}

export function summarize(meta: MetaLike | null, artifacts: ArtifactLike[]): Summary {
  const isMerged = !!meta && meta.event === 'pr_merged';

  const sortedArtifacts = [...artifacts]
    .filter((a) => typeof a.ts === 'string')
    .sort((a, b) => (a.ts as string).localeCompare(b.ts as string));

  const latest = sortedArtifacts.at(-1);
  const latestVerdict = latest && typeof latest.verdict_state === 'string' ? latest.verdict_state : null;

  const totalCostUsd = sortedArtifacts.reduce(
    (sum, a) => sum + (typeof a.cost_usd === 'number' ? a.cost_usd : 0),
    0
  );

  // Title source: prefer the META row (only written by gh-webhook when a PR
  // is closed-and-merged — see ingest-gh-webhook.ts), fall back to the
  // most-recent artifact's title (set by the diff-vader runner via octokit
  // on every council run, including for open PRs). Author and branch only
  // come from artifact rows.
  const metaTitle = meta && typeof meta.title === 'string' ? meta.title : null;
  const latestTitle = latest && typeof latest.title === 'string' ? latest.title : null;
  const latestAuthor = latest && typeof latest.author === 'string' ? latest.author : null;
  const latestBranch = latest && typeof latest.branch === 'string' ? latest.branch : null;

  return {
    isMerged,
    mergedAt: meta && typeof meta.merged_at === 'string' ? meta.merged_at : null,
    mergedBy: meta && typeof meta.merged_by === 'string' ? meta.merged_by : null,
    title: metaTitle ?? latestTitle,
    author: latestAuthor,
    branch: latestBranch,
    latestVerdict,
    totalCostUsd,
    runsCount: sortedArtifacts.length,
  };
}

function fmtUsd(n: number): string {
  // 2 decimals — sub-cent precision adds noise without insight.
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMergedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

const verdictPillColor: Record<string, { bg: string; fg: string }> = {
  APPROVE: { bg: 'var(--loop-green-100)', fg: 'var(--loop-green-700)' },
  NEEDS_HUMAN: { bg: 'var(--loop-yellow-100)', fg: 'var(--loop-yellow-800)' },
  REQUEST_CHANGES: { bg: 'var(--loop-red-100, #fde2e0)', fg: 'var(--loop-red-700, #a8201a)' },
  TOO_LARGE: { bg: 'var(--loop-neutral-200)', fg: 'var(--loop-neutral-700)' },
};

function StatusPill({ label, kind }: { label: string; kind: 'merged' | 'open' }) {
  const colors =
    kind === 'merged'
      ? { bg: 'var(--loop-purple-100, #ede4f5)', fg: 'var(--loop-purple-700, #6f3eb1)' }
      : { bg: 'var(--loop-green-100)', fg: 'var(--loop-green-700)' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 528,
        background: colors.bg,
        color: colors.fg,
      }}
    >
      {label}
    </span>
  );
}

function VerdictPill({ verdict }: { verdict: string }) {
  const colors = verdictPillColor[verdict] ?? { bg: 'var(--loop-neutral-200)', fg: 'var(--loop-neutral-700)' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        fontSize: '0.75rem',
        fontWeight: 528,
        background: colors.bg,
        color: colors.fg,
      }}
    >
      {verdict}
    </span>
  );
}

export interface PrStatusCardProps {
  meta: MetaLike | null;
  artifacts: ArtifactLike[];
  // Optional PR coordinates for linking the title to the GitHub PR. When all
  // three are present, the title renders as an anchor pointing at github.com.
  org?: string;
  repo?: string;
  pr?: string | number;
}

export function PrStatusCard({ meta, artifacts, org, repo, pr }: PrStatusCardProps) {
  const s = summarize(meta, artifacts);
  const ghUrl = org && repo && pr ? `https://github.com/${org}/${repo}/pull/${pr}` : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <StatusPill label={s.isMerged ? 'Merged' : 'Open'} kind={s.isMerged ? 'merged' : 'open'} />
        {s.latestVerdict && <VerdictPill verdict={s.latestVerdict} />}
      </div>

      {s.title && (
        <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)', fontWeight: 528, marginBottom: 8 }}>
          {ghUrl ? (
            // Underlined for a11y — color-only link affordance fails for
            // color-blind users (WCAG). The `↗` glyph adds the external-link
            // cue on top.
            <a
              href={ghUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--loop-blue-600)' }}
            >
              {s.title} <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>↗</span>
            </a>
          ) : (
            s.title
          )}
        </p>
      )}

      <table style={{ fontSize: '0.875rem', width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
        <tbody>
          {s.author && (
            <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              <td style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Author</td>
              <td style={{ paddingTop: 4, paddingBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>@{s.author}</span>
              </td>
            </tr>
          )}
          {s.branch && (
            <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              <td style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Branch</td>
              <td style={{ paddingTop: 4, paddingBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.branch}</span>
              </td>
            </tr>
          )}
          {s.isMerged && (
            <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              <td style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Merged by</td>
              <td style={{ paddingTop: 4, paddingBottom: 4 }}>
                {s.mergedBy ? <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>@{s.mergedBy}</span> : <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>}
                {s.mergedAt && <span style={{ color: 'var(--loop-neutral-600)', marginLeft: 6 }}>· on {fmtMergedAt(s.mergedAt)}</span>}
              </td>
            </tr>
          )}
          <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
            <td style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Council runs</td>
            <td style={{ paddingTop: 4, paddingBottom: 4 }}>{s.runsCount}</td>
          </tr>
          <tr style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
            <td style={{ paddingTop: 4, paddingBottom: 4, paddingRight: 16, width: 160, color: 'var(--loop-neutral-600)', fontWeight: 528 }}>Total cost</td>
            <td style={{ paddingTop: 4, paddingBottom: 4 }}>{fmtUsd(s.totalCostUsd)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
